import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useFormik } from 'formik';
import { doctorAPI } from '../../services/api';
import { doctorSchema } from '../../validations/schemas';
import { toast } from 'react-toastify';

const DoctorSearchAndRegister = ({ value, onChange, error, helperText }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  // --- Main data fetching logic, inspired by your original implementation ---
  useEffect(() => {
    // This flag prevents state updates on unmounted components
    let active = true;

    // We only fetch when the dropdown is open
    if (!open) {
      return undefined;
    }

    const fetchDoctors = async () => {
      setLoading(true);
      try {
        let response;
        if (searchQuery) {
          response = await doctorAPI.search(searchQuery);
        } else {
          // ** FIX: Restore fetching all doctors when the query is empty **
          response = await doctorAPI.getAll({ page: 1, limit: 100 });
          console.log('Fetched all doctors:', response.data);
        }

        if (active) {
          let fetchedDoctors = [];
          if (response.data?.data?.doctors) { // From getAll
            fetchedDoctors = response.data.data.doctors;
          } else if (response.data?.data && Array.isArray(response.data.data)) { // From search
            fetchedDoctors = response.data.data;
          }

          let finalOptions = Array.isArray(fetchedDoctors) ? fetchedDoctors : [];

          // ** FIX: Robustly handle the initial value for edit forms **
          // Ensure the currently selected doctor is always in the list to be displayed.
          if (value) {
            // Check if the current value is already in the fetched list
            const valueExists = finalOptions.some(opt => opt._id === value._id);
            if (!valueExists) {
              // If not, add it to the top of the list
              finalOptions.unshift(value);
            }
          }
          setOptions(finalOptions);
        }
      } catch (err) {
        console.error('Error fetching doctors:', err);
        if (active) {
          setOptions(value ? [value] : []); // On error, at least show the selected value
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    // Simple debounce with setTimeout
    const timeoutId = setTimeout(fetchDoctors, 300);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [searchQuery, open, value]); // React to query changes, opening, and value changes

  // Formik for the "Register New Doctor" modal
  const formik = useFormik({
    initialValues: { name: '', specialization: '', licenseNumber: '', contactNumber: '' },
    validationSchema: doctorSchema,
    onSubmit: async (values) => {
      try {
        setRegisterLoading(true);
        const response = await doctorAPI.create(values);
        const newDoctor = response.data.data;
        toast.success(t('doctors.createSuccess'));

        // Add new doctor to options and automatically select it
        setOptions((prev) => [newDoctor, ...prev]);
        onChange(newDoctor);

        setShowRegisterForm(false);
        formik.resetForm();
      } catch (err) {
        toast.error(err.response?.data?.message || t('doctors.error'));
      } finally {
        setRegisterLoading(false);
      }
    },
  });

  const handleOpenRegister = () => setShowRegisterForm(true);
  const handleCloseRegister = () => {
    setShowRegisterForm(false);
    formik.resetForm();
  };

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={(event, newValue) => onChange(newValue)}
        onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
        isOptionEqualToValue={(option, val) => option?._id === val?._id}
        getOptionLabel={(option) => {
          if (!option) return '';
          const specialization = typeof option.specialization === 'object' ? option.specialization?.name : option.specialization || 'No specialization';
          return `Dr. ${option.name || 'Unknown'} - ${specialization}`;
        }}
        options={options}
        loading={loading}
        filterOptions={(x) => x} // Rely on API for filtering
        renderInput={(params) => (
          <TextField {...params} label={t('patients.selectDoctor')} error={error} helperText={helperText}
            InputProps={{ ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option._id}>
            <Box>
              <Typography variant="body1">Dr. {option?.name || 'Unknown'}</Typography>
              <Typography variant="body2" color="text.secondary">{typeof option.specialization === 'object' ? option.specialization?.name : option.specialization || 'No specialization'}</Typography>
            </Box>
          </Box>
        )}
        noOptionsText={
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {searchQuery ? t('doctors.noDoctorsFound') : t('doctors.startTyping')}
            </Typography>
            <Button size="small" onClick={handleOpenRegister} sx={{ mt: 1 }}>{t('doctors.registerNew')}</Button>
          </Box>
        }
      />

      <Dialog open={showRegisterForm} onClose={handleCloseRegister} maxWidth="sm" fullWidth>
        <DialogTitle>{t('doctors.registerNew')}</DialogTitle>
        <DialogContent>
          <Box component="form" id="doctor-register-form" onSubmit={formik.handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth label={t('doctors.name')} name="name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.name && Boolean(formik.errors.name)} helperText={formik.touched.name && formik.errors.name} /></Grid>
              <Grid item xs={12}><TextField fullWidth label={t('doctors.specialization')} name="specialization" value={formik.values.specialization} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.specialization && Boolean(formik.errors.specialization)} helperText={formik.touched.specialization && formik.errors.specialization} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t('doctors.licenseNumber')} name="licenseNumber" value={formik.values.licenseNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.licenseNumber && Boolean(formik.errors.licenseNumber)} helperText={formik.touched.licenseNumber && formik.errors.licenseNumber} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t('doctors.contactNumber')} name="contactNumber" value={formik.values.contactNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.contactNumber && Boolean(formik.errors.contactNumber)} helperText={formik.touched.contactNumber && formik.errors.contactNumber} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegister}>{t('common.cancel')}</Button>
          <Button type="submit" form="doctor-register-form" variant="contained" disabled={registerLoading} startIcon={registerLoading ? <CircularProgress size={20} /> : null}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DoctorSearchAndRegister;